<script lang="ts" setup>
import { Money } from '@commercetools/platform-sdk';
import { dollars } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { useVModel } from '@vueuse/core';
import { computed, ref, watch } from 'vue';

import { getGiftCertificate, GiftCertificateResponse } from '@/api/giftCertificates';
import Checkbox from '@/components/base/form/Checkbox.vue';
import FloatingLabel from '@/components/base/form/FloatingLabel.vue';
import InlineError, { InputFailedEvent } from '@/components/base/form/InlineError.vue';
import Input from '@/components/base/form/Input.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import { useCallback } from '@/composables/useCallback';
import money from '@/filters/money';
import { CheckoutEvents } from '@/utils/analytics';

interface Props {
  estimatedGiftCertificateCharge?: Money;
  giftCertificate?: GiftCertificateResponse;
  modelValue: boolean;
  stepNumber?: number;
}

const props = withDefaults(defineProps<Props>(), { stepNumber: 4 });
const emit = defineEmits<{
  'set-gift-certificate': [gc?: GiftCertificateResponse];
  'update:modelValue': [boolean];
}>();

const errorMessage = ['Please enter a valid code'];
const showValidationError = ref(false);
const code = ref('');

const useGiftCertificate = useVModel(props, 'modelValue', emit);

const onApply = useCallback(async () => {
  if (code.value.length < 12) {
    showValidationError.value = true;
    return undefined;
  }
  showValidationError.value = false;
  CheckoutEvents.option(4, 'Gift Certificate');
  CheckoutEvents.event({ action: 'Payment', label: 'Applied Gift Certificate' });
  const gc = await getGiftCertificate(code.value);
  emit('set-gift-certificate', gc);
  return gc;
});

const onInputChange = () => {
  showValidationError.value = false;
};

const onInputFailed = ({ field, error }: InputFailedEvent) => {
  CheckoutEvents.validation({ stepNumber: props.stepNumber, field, error });
};

if (props.giftCertificate) {
  code.value = props.giftCertificate.code;
  onApply.result = props.giftCertificate;
}

watch(
  () => props.modelValue,
  () => {
    // if we have a GC in hand, add/remove it upstream when the checkbox changes
    if (onApply.result) {
      emit('set-gift-certificate', props.modelValue ? onApply.result : undefined);
    }
    if (props.modelValue) {
      CheckoutEvents.option(4, 'Gift Certificate');
      CheckoutEvents.event({ action: 'Payment', label: 'Payment Method: Gift Certificate' });
    }
  },
);

const selectGiftCertificate = () => {
  if (!useGiftCertificate.value) {
    useGiftCertificate.value = true;
  }
};

const onRemove = () => {
  onApply.result = undefined;
  emit('set-gift-certificate');
};

const showRemoveButton = computed(() => dollars(onApply.result?.remainingValue) > 0);
const showError = computed(() => showValidationError.value || !!onApply.error);
const giftIcon = nutshell['img/present-yellow-blue.svg'];
</script>

<template>
  <div class="flex-col" @click.self="selectGiftCertificate" @keydown.enter="selectGiftCertificate">
    <Checkbox v-model="useGiftCertificate" data-test="gift-certificate-selector">
      <div
        class="w-full leading-6 cursor-pointer md:leading-7"
        :class="useGiftCertificate ? 'font-semibold' : 'font-normal'"
      >
        Pay with Gift Certificate
        <img aria-hidden="true" :src="giftIcon" class="ml-1 w-9 md:w-11" alt="gift icon" />
      </div>
    </Checkbox>
    <div :class="useGiftCertificate ? 'h-24 mt-7' : 'hidden'">
      <div class="flex items-center">
        <div class="relative w-2/3">
          <Input
            v-model="code"
            type="text"
            name="giftCertificate"
            id="giftCertificate"
            placeholder="Gift certificate code"
            required
            aria-label="Gift certificate code"
            :invalid="showError"
            data-test="gift-certificate-code"
            @input="onInputChange"
          />
          <FloatingLabel
            class="left-0"
            :invalid="showError"
            data-test="gift-certificate-code-label"
          >
            Gift Certificate Code*
          </FloatingLabel>
          <InlineError
            v-if="showError"
            :errors="errorMessage"
            data-test="gift-certificate-code-validation-error"
            fieldName="Gift Certificate Code"
            @input-failed="onInputFailed"
          />
        </div>
        <ThemedButton
          class="ml-3"
          theme="gray"
          data-test="gift-certificate-submit"
          :isLoading="onApply.isPending"
          @click="onApply.execute"
        >
          Apply
        </ThemedButton>
      </div>
      <div v-if="onApply.result" class="flex items-center mt-5">
        <div class="font-semibold leading-6 md:leading-7">
          <template v-if="onApply.result.remainingValue.centAmount === 0">
            Oops! Your gift certificate balance is $0
          </template>
          <template v-else-if="estimatedGiftCertificateCharge">
            {{ money(estimatedGiftCertificateCharge) }} will be applied to your order
          </template>
        </div>
        <div
          v-if="showRemoveButton"
          class="ml-4 text-sm underline cursor-pointer"
          @click="onRemove"
        >
          Remove
        </div>
      </div>
    </div>
  </div>
</template>
